/**
 * «Сайт и контент» без вопросов, но с открытыми темами: панель фильтров (меню
 * выгрузки — блок тем, #2009), пояснение, блок тем (топ-5, #2072).
 * Выгрузка — весь набор, без суффикса темы: темой здесь ничто не сужено.
 * Набор тем пуст — как без тем; пустоту решает список набора, а не строки
 * аналитики окна (#2094). Суженных темой запросов здесь нет: выбранную тему,
 * которой нет среди активных (архивная из ссылки), снимает список набора
 * (#2238). Список, прочитанный раньше открытия вкладки, ей не верят, когда
 * строки окна выбранной темы не содержат: он мог устареть (#2902).
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StreamFilterBar } from '../stream-filter/StreamFilterBar';
import { useStreamDemoFilter } from '../stream-filter/useStreamDemoFilter';
import { useStreamWindow } from '../stream-filter/useStreamWindow';
import { useVoiceThemes } from './themes/useVoiceThemes';
import { VoiceExportMenu } from './VoiceExportMenu';
import { VoiceLocked } from './VoiceLocked';
import { tierRefusalCode } from '@/lib/tier-refusal';
import { VoiceSiteClosedNotice } from './VoiceSiteClosedNotice';
import { VoiceThemesPanel } from './VoiceThemesPanel';
import { VOICE_TOP_ROWS } from './VoiceTopList';
import { useVoiceThemeAnalytics } from './useVoiceOfCustomer';
import { tabSeriesWindow, VoiceSeriesWindowContext } from './useVoiceSeries';
import { useReportThemeRefusal } from './voice-theme-filter-context';
import {
  themeListRefusalCode,
  themeRowsLackTheme,
} from './voice-theme-filter-view-model';
import {
  themeExportSection,
  themePrintNotes,
  themeSectionSetEmpty,
  themeSetEmpty,
  themesState,
} from './voice-themes-view-model';
import type { VoiceSiteClosedProps } from './VoiceSiteClosed';

export function VoiceSiteClosedWithThemes({
  reason,
  search,
  onSearchChange,
  onOpenCall,
  upgradeHref,
  onOpenThemes,
}: VoiceSiteClosedProps) {
  const { t } = useTranslation('dashboard');
  const { period, navigation, projectIds, callCategory, dateRange } = useStreamWindow(
    search,
    onSearchChange
  );
  // The panel's own query (same key, one request): the filter bar's loading
  // indicator follows the only data this tab has.
  const themes = useVoiceThemeAnalytics(search, onSearchChange, 'questions', true);
  const list = useVoiceThemes();
  // No request here is narrowed by the theme, so no server refuses it: the
  // tab says it by its own answers (#2238). The themes answer closed by the
  // server is the flag off — `themes_disabled`, as every other tab hears it. A
  // theme the set's list does not hold active (archived from a link) — only
  // by a list read since the tab opened: a cached one may predate a theme
  // created or restored in another session, so it is read again first.
  const state = !themes.isError && themes.data ? themesState(themes.data) : null;
  const rows = state?.kind === 'ready' ? state.themes.rows : undefined;
  const listRefusal = themeListRefusalCode(search.voiceTheme, list.data?.themes, rows);
  const { refetch: refetchList, isFetchedAfterMount, isFetching, isError } = list;
  const listCurrent = isFetchedAfterMount && !isFetching && !isError;
  // The list is also read again when the fresh rows do not hold the selected
  // theme: a cached one inside its `staleTime` may still call it active (#2902).
  const listDoubted = listRefusal !== null || themeRowsLackTheme(search.voiceTheme, rows);
  useEffect(() => {
    if (listDoubted && !isFetchedAfterMount && !isFetching) void refetchList();
  }, [listDoubted, isFetchedAfterMount, isFetching, refetchList]);
  useReportThemeRefusal(
    search.voiceTheme,
    state?.kind === 'closed' ? 'themes_disabled' : listCurrent ? listRefusal : null
  );
  const { includeDemo } = useStreamDemoFilter(search, 'voice-section');
  // The section refused this request (403 with a code): this tab's only request
  // to the section, so nobody else says it — the refusal instead of the tab.
  const refusal = themes.isError ? tierRefusalCode(themes.error) : null;
  if (refusal !== null) return <VoiceLocked code={refusal} upgradeHref={upgradeHref} />;
  // The server closed the themes gate (the client's flag map is behind it):
  // the tab as it is without themes — no filter bar over a block that is not there.
  // A failed refetch keeps the closed answer in `data`: it falls through, and the
  // panel shows the error with a retry.
  // An empty set is the same on this tab: no block (§2.6, #2072) — the header's
  // «Set themes» says it, and the notice does not promise themes below. The set
  // list says it, as in the tabs by layers: the last active theme archived, and
  // the window's rows are still the old ones until the re-read ends (#2094).
  const setEmpty = themeSectionSetEmpty({
    ready: state?.kind === 'ready' ? state.themes : undefined,
    demoShown: includeDemo === 'yes',
    list: list.data ? list.data.themes : list.isError ? 'failed' : undefined,
  });
  // Either says it: the list (the last theme archived, the window's rows still
  // the old ones) or the rows (the list is still being read, or is behind: a
  // notice that promises themes below must have a block below).
  const rowsEmpty = state?.kind === 'ready' && themeSetEmpty(state.themes);
  if (state?.kind === 'closed' || setEmpty === true || rowsEmpty) {
    return (
      <VoiceSiteClosedNotice
        reason={reason}
        withThemes={false}
        upgradeHref={upgradeHref}
      />
    );
  }
  return (
    <div className="space-y-4">
      <StreamFilterBar
        period={period}
        navigation={navigation}
        projectIds={projectIds}
        callCategory={callCategory}
        isLoading={themes.isFetching && !themes.isLoading}
        onSearchChange={onSearchChange}
        // The tab's export is its themes block (#2009): the block the screen
        // draws as failed or still loading holds the menu back.
        extraActions={
          state?.kind === 'ready' && themes.data ? (
            <VoiceExportMenu
              audience="site"
              sections={[themeExportSection(state.themes)]}
              data={{
                kind: 'questions',
                periodStart: themes.data.periodStart,
                periodEnd: themes.data.periodEnd,
                // The block carries its own cap (`VoiceSection.cap`).
                truncated: false,
                prevTruncated: false,
              }}
              printNotes={themePrintNotes(state.themes, t)}
              // A new filter keeps no previous answer: the menu waits instead.
              disabled={false}
            />
          ) : (
            <VoiceExportMenu
              unavailable={
                themes.isError || state?.kind === 'failed' ? 'failed' : 'pending'
              }
            />
          )
        }
      />
      <VoiceSiteClosedNotice reason={reason} withThemes upgradeHref={upgradeHref} />
      {/* The themes' series (#2394 §9 п. 7) by this tab's window. */}
      <VoiceSeriesWindowContext.Provider value={tabSeriesWindow(dateRange, [themes])}>
        <VoiceThemesPanel
          cut="questions"
          search={search}
          onSearchChange={onSearchChange}
          onOpenCall={onOpenCall}
          onOpenThemes={onOpenThemes}
          limit={VOICE_TOP_ROWS}
        />
      </VoiceSeriesWindowContext.Provider>
    </div>
  );
}
